import React, { useState } from 'react';
import { useAuth } from './auth';
import { CREDIT_LINE, DEVELOPER } from './about';
import './login.css';

const WHO = [
  { value: 'none', label: 'الطالب' },
  { value: 'father', label: 'الأب' },
  { value: 'mother', label: 'الأم' },
];

/**
 * Three steps: name -> (pick the right account) -> code.
 *
 * The role is fixed to `student`; the only question is WHO is signing
 * in for that pupil, which maps to parent_type. When two pupils share a
 * name the API returns candidates with a `label`: the class for a pupil,
 * the children + classes for a parent (masked phone only if still alike).
 */
export default function Login() {
  const { lookup, select, verify } = useAuth();
  const [step, setStep] = useState('name');
  const [name, setName] = useState('');
  const [who, setWho] = useState('none');
  const [candidates, setCandidates] = useState([]);
  const [userId, setUserId] = useState(null);
  const [masked, setMasked] = useState('');
  const [devCode, setDevCode] = useState('');
  const [isAccessCode, setIsAccessCode] = useState(false);
  const [smsFailed, setSmsFailed] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const applySent = (data) => {
    setUserId(data.user_id);
    setMasked(data.phone_masked || '');
    setDevCode(data.dev_code || '');
    // Someone with a standing رمز دخول never gets an SMS (they usually
    // can't receive one), so the screen must ask for that code instead
    // of claiming a message was sent.
    setIsAccessCode(!!data.access_code);
    setSmsFailed(!!data.sms_failed);
    setStep('code');
  };

  const submitName = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('يرجى إدخال الاسم');
    setBusy(true);
    try {
      const data = await lookup(name.trim(), who);
      if (data.ambiguous) { setCandidates(data.candidates); setStep('choose'); }
      else applySent(data);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  const pick = async (candidateId) => {
    setError(''); setBusy(true);
    try { applySent(await select(candidateId)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!code.trim()) return setError('يرجى إدخال الرمز');
    setBusy(true);
    try { await verify(userId, code.trim()); }
    catch (err) {
      setError(err.message);
      // Wrong code: stay here with an empty box so they can try again.
      if (err.code === 'INVALID_CODE') setCode('');
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">م</div>
          <h1>بوابة الطالب</h1>
          <p>مؤسسة المتميزون التعليمية</p>
        </div>

        {error && <div className="login-error">{error}</div>}

        {step === 'name' && (
          <form onSubmit={submitName} className="login-form">
            {/* Who first: a parent's account is found by the PARENT's
                name, so the name label has to follow this choice. */}
            <span className="login-label">من يسجّل الدخول؟</span>
            <div className="login-who">
              {WHO.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`login-who-btn${who === o.value ? ' is-active' : ''}`}
                  onClick={() => setWho(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <label className="login-label" htmlFor="login-name">
              {who === 'father' ? 'اسم الأب والكنية' : who === 'mother' ? 'اسم الأم والكنية' : 'اسم الطالب'}
            </label>
            <input
              id="login-name"
              className="login-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={who === 'none' ? 'كما هو مسجّل في المدرسة' : 'مثال: أحمد الحلبي'}
              autoFocus
            />

            <button type="submit" className="ui-btn ui-btn-block" disabled={busy}>
              {busy && <span className="ui-spinner-sm" aria-hidden="true" />}
              {busy ? 'جارٍ البحث...' : 'متابعة'}
            </button>
          </form>
        )}

        {step === 'choose' && (
          <div className="login-form">
            <p className="login-hint">يوجد أكثر من حساب بهذا الاسم، {who === 'none' ? 'اختر الصف الصحيح:' : 'اختر حسابك حسب أبنائك:'}</p>
            {candidates.map((c) => (
              <button
                key={c.user_id}
                type="button"
                className="login-candidate"
                disabled={busy}
                onClick={() => pick(c.user_id)}
              >
                {c.label || (c.grade_title ? `${c.grade_title} — ${c.section_title || ''}` : `حساب #${c.user_id}`)}
              </button>
            ))}
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-block" onClick={() => setStep('name')}>
              رجوع
            </button>
          </div>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} className="login-form">
            <p className="login-hint">
              {isAccessCode
                ? 'أدخل رمز الدخول الذي أعطاك إياه مكتب المدرسة.'
                : smsFailed ? 'تعذّر إرسال رسالة إلى الهاتف المسجّل. إن كان لديك رمز فأدخله، أو راجع إدارة المدرسة.'
                : `تم إرسال رمز التحقق إلى الهاتف ${masked}`}
              {devCode && <><br /><strong>رمز التطوير: {devCode}</strong></>}
            </p>
            <label className="login-label" htmlFor="login-code">
              {isAccessCode ? 'رمز الدخول' : 'رمز التحقق'}
            </label>
            <input
              id="login-code"
              className="login-input login-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              dir="ltr"
            />
            <button type="submit" className="ui-btn ui-btn-block" disabled={busy}>
              {busy && <span className="ui-spinner-sm" aria-hidden="true" />}
              {busy ? 'جارٍ التحقق...' : 'تسجيل الدخول'}
            </button>
            <button
              type="button"
              className="ui-btn ui-btn-secondary ui-btn-block"
              onClick={() => { setStep('name'); setCode(''); }}
            >
              تغيير الاسم
            </button>
          </form>
        )}
      </div>
      <footer className="login-credit">
        <a href={DEVELOPER.website} target="_blank" rel="noopener noreferrer">{CREDIT_LINE}</a>
      </footer>
    </div>
  );
}
